import React, { useState } from 'react';
import {
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Grid,
  Button,
  makeStyles,
} from '@material-ui/core';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import ViewListIcon from '@material-ui/icons/ViewList';
import RefreshIcon from '@material-ui/icons/Refresh';
import SettingsIcon from '@material-ui/icons/Settings';
import { Table, TableColumn, Progress } from '@backstage/core-components';
import { Alert } from '@material-ui/lab';
import { SimpleResourceCard } from './SimpleResourceCard';
import {
  ResourceTableDataType,
  CRDCloudService,
  CRDResourceType,
} from './types';

interface ModernResourceViewProps<T extends ResourceTableDataType> {
  title: string;
  data: T[];
  isLoading: boolean;
  onRefresh: () => void;
  onActions: (event: React.MouseEvent) => void;
  cloudService: CRDCloudService;
  resourceType: CRDResourceType;
  columns: TableColumn<T>[];
  colorTheme: string;
  emptyMessage?: string;
}

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: 8,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(2),
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    fontWeight: 'bold',
    marginRight: theme.spacing(2),
  },
  resourceCount: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    borderRadius: 4,
    fontWeight: 'bold',
    fontSize: '0.75rem',
  },
  actionsContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  viewButton: {
    minWidth: 40,
    marginRight: theme.spacing(1),
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  },
}));

export const ModernResourceView = <T extends ResourceTableDataType>({
  title,
  data,
  isLoading,
  onRefresh,
  onActions,
  cloudService,
  resourceType,
  columns,
  colorTheme,
  emptyMessage = 'No resources found',
}: ModernResourceViewProps<T>) => {
  const classes = useStyles();
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');

  const resourceCount = data.length;

  const headerStyle = {
    borderBottom: `2px solid ${colorTheme}20`,
  };

  const emptyMessageStyle = {
    backgroundColor: `${colorTheme}08`,
  };

  return (
    <Paper className={classes.paper}>
      {/* Header */}
      <div className={classes.header} style={headerStyle}>
        <div className={classes.titleContainer}>
          <Typography
            variant="h5"
            className={classes.title}
            style={{ color: colorTheme }}
          >
            {title}
          </Typography>
          <div
            className={classes.resourceCount}
            style={{
              backgroundColor: `${colorTheme}20`,
              color: colorTheme,
            }}
          >
            {resourceCount} resource{resourceCount !== 1 ? 's' : ''}
          </div>
        </div>

        <div className={classes.actionsContainer}>
          <Button
            className={classes.viewButton}
            variant={viewMode === 'cards' ? 'contained' : 'outlined'}
            color={viewMode === 'cards' ? 'primary' : 'default'}
            onClick={() => setViewMode('cards')}
            size="small"
          >
            <Tooltip title="Card View">
              <ViewModuleIcon />
            </Tooltip>
          </Button>

          <Button
            className={classes.viewButton}
            variant={viewMode === 'table' ? 'contained' : 'outlined'}
            color={viewMode === 'table' ? 'primary' : 'default'}
            onClick={() => setViewMode('table')}
            size="small"
          >
            <Tooltip title="Table View">
              <ViewListIcon />
            </Tooltip>
          </Button>

          <Tooltip title="Refresh Resources">
            <IconButton onClick={onRefresh} className={classes.actionButton}>
              <RefreshIcon style={{ color: colorTheme }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Actions">
            <IconButton onClick={onActions} className={classes.actionButton}>
              <SettingsIcon style={{ color: colorTheme }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Content */}
      {resourceCount === 0 && !isLoading ? (
        <Alert severity="info" style={emptyMessageStyle}>
          {emptyMessage}
        </Alert>
      ) : (
        <>
          {viewMode === 'cards' ? (
            isLoading ? (
              <Progress />
            ) : (
              <Grid container spacing={2}>
                {data.map((resource, index) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                    key={`${cloudService}-${resourceType}-${
                      resource.identifier || index
                    }`}
                  >
                    <SimpleResourceCard
                      resource={resource}
                      columns={columns}
                      cloudService={cloudService}
                      resourceType={resourceType}
                      colorTheme={colorTheme}
                    />
                  </Grid>
                ))}
              </Grid>
            )
          ) : (
            <Table
              options={{
                search: true,
                paging: true,
                pageSize: 5,
                padding: 'dense',
              }}
              columns={columns}
              data={data}
              isLoading={isLoading}
            />
          )}
        </>
      )}
    </Paper>
  );
};
